import { queryOptions } from "@tanstack/react-query";
import type {
  LocationId,
  UserLocationDto,
  UserRole as ContractUserRole,
} from "@yalana/contracts";
import { fetchApi } from "../../../shared/infrastructure/api-client";

export interface UserProfile {
  id: string;
  email: string;
  name: string;
  role: string;
  // ADR-035: platform authority (BEL-477) — never gate UI on `role` string.
  platformAdmin: boolean;
  customerIds: string[];
  tenantNames: string[];
  createdAt: string;
  updatedAt: string;
}

export interface UserRole {
  roleName: string;
  roleEntityId: string;
  customerId: string;
  customerName: string;
}

interface UserProfileWithRoles extends UserProfile {
  roles: UserRole[];
  locationIds: LocationId[];
  entityId: string | null;
}

interface PaginationParams {
  page: number;
  pageSize: number;
  q?: string;
  sort?: string;
  dir?: "asc" | "desc";
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
}

export const userAdminQueries = {
  all: () => ["users"] as const,
  list: (params: PaginationParams = { page: 1, pageSize: 10 }) =>
    queryOptions({
      queryKey: [...userAdminQueries.all(), "list", params] as const,
      queryFn: () => {
        const searchParams: Record<string, string> = {
          page: params.page.toString(),
          pageSize: params.pageSize.toString(),
        };
        if (params.q) searchParams.q = params.q;
        if (params.sort) searchParams.sort = params.sort;
        if (params.dir) searchParams.dir = params.dir;
        return fetchApi<PaginatedResponse<UserProfile>>("/api/admin/users", {
          searchParams,
        });
      },
    }),
  detail: (userId: string) =>
    queryOptions({
      queryKey: [...userAdminQueries.all(), userId] as const,
      queryFn: () =>
        fetchApi<UserProfileWithRoles>(`/api/admin/users/${userId}`),
      enabled: !!userId,
    }),
  locations: (userId: string) =>
    queryOptions({
      queryKey: [...userAdminQueries.all(), userId, "locations"] as const,
      queryFn: () => fetchApi<LocationId[]>(`/api/user-locations/${userId}`),
      enabled: !!userId,
    }),
};

export const userAdminMutations = {
  invite: () => ({
    mutationFn: (data: {
      email: string;
      name: string;
      role?: string;
      // BEL-732: which customer the invitee joins. Omitted means "the
      // caller's own", which the API resolves.
      customerId?: string;
    }) =>
      fetchApi<{ invitationLink: string }>("/api/auth/admin/invite", {
        method: "POST",
        body: JSON.stringify(data),
      }),
  }),
  deactivate: (userId: string) => ({
    mutationFn: () =>
      fetchApi<{ success: boolean }>(
        `/api/auth/admin/users/${userId}/deactivate`,
        { method: "PATCH" },
      ),
  }),
  update: (userId: string) => ({
    mutationFn: (data: { name: string }) =>
      fetchApi<{ success: boolean }>(`/api/admin/users/${userId}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
  }),
  addRole: (userId: string) => ({
    mutationFn: (roleEntityId: string) =>
      fetchApi<{ success: boolean }>(`/api/auth/admin/users/${userId}/roles`, {
        method: "POST",
        body: JSON.stringify({ roleEntityId }),
      }),
  }),
  removeRole: (userId: string) => ({
    mutationFn: (roleEntityId: string) =>
      fetchApi<{ success: boolean }>(
        `/api/auth/admin/users/${userId}/roles/${roleEntityId}`,
        { method: "DELETE" },
      ),
  }),
  assignCustomer: (userId: string) => ({
    mutationFn: ({
      customerId,
      role,
    }: {
      customerId: string;
      role: ContractUserRole;
    }) =>
      fetchApi<{ success: boolean }>(
        `/api/customers/${customerId}/users/${userId}/assign`,
        { method: "POST", body: JSON.stringify({ role }) },
      ),
  }),
  removeCustomer: (userId: string) => ({
    mutationFn: (customerId: string) =>
      fetchApi<void>(`/api/customers/${customerId}/users/${userId}`, {
        method: "DELETE",
      }),
  }),
  syncJwt: (userId: string) => ({
    mutationFn: () =>
      fetchApi<{ success: boolean; entityIds: string[] }>(
        `/api/auth/admin/users/${userId}/sync-jwt`,
        { method: "POST" },
      ),
  }),
  setEntity: (userId: string) => ({
    mutationFn: (entityId: string | null) =>
      fetchApi<{ success: boolean }>(`/api/admin/users/${userId}`, {
        method: "PATCH",
        body: JSON.stringify({ entityId }),
      }),
  }),
  setUserLocations: (userId: string) => ({
    mutationFn: ({
      customerId,
      locationIds,
    }: {
      customerId: string;
      locationIds: string[];
    }) =>
      fetchApi<UserLocationDto[]>(`/api/user-locations/${userId}`, {
        method: "PUT",
        body: JSON.stringify({ customerId, locationIds }),
      }),
  }),
};
